'use client'

import DOMPurify from 'dompurify'
import {
    EmailAccount,
    EmailAttachmentInfo,
    EmailDraftDetail,
    EmailDraftSummary,
    EmailFolder,
    EmailMessageDetail,
    EmailMessageSummary,
    archiveEmail,
    getAttachmentDownloadUrl,
    getDraft,
    getDrafts,
    getEmailAccounts,
    getEmailMessage,
    getEmailMessages,
    getEmailThread,
    toggleEmailReadStatus,
    toggleEmailStar,
    trashEmail,
    untrashEmail,
} from '@/lib/api/administrative/inbox-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { format, isToday, isYesterday } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
    MdAttachFile,
    MdChevronLeft,
    MdClose,
    MdDownload,
    MdEmail,
    MdMailOutline,
    MdNavigateBefore,
    MdNavigateNext,
    MdRefresh,
    MdSettings,
    MdStar,
    MdDescription,
    MdStarBorder,
} from 'react-icons/md'
import { FaGoogle } from 'react-icons/fa6'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ComposeModal, {
    type ComposeMode,
} from './components/compose-modal'
import FolderSidebar from './components/folder-sidebar'
import MessageActions from './components/message-actions'
import SearchBar from './components/search-bar'
import ThreadView from './components/thread-view'

type ReadFilter = 'all' | 'unread' | 'read'

const FOLDER_TITLES: Record<EmailFolder, string> = {
    INBOX: 'Caixa de Entrada',
    STARRED: 'Com Estrela',
    SENT: 'Enviados',
    DRAFT: 'Rascunhos',
    TRASH: 'Lixeira',
    ALL: 'Todos os Emails',
}

function formatMessageDate(dateStr?: string): string {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isToday(date)) {
        return format(date, 'HH:mm')
    }
    if (isYesterday(date)) {
        return 'Ontem'
    }
    return format(date, 'dd/MM/yyyy')
}

function formatFullDate(dateStr?: string): string {
    if (!dateStr) return ''
    return format(new Date(dateStr), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
        locale: ptBR,
    })
}

function formatFileSize(bytes?: number): string {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function InboxPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { canCreate, canUpdate } = useResourcePermissions(
        'administrative',
        'inbox',
    )
    const { socket } = useContext(SocketContext)
    const { user } = useAuth()

    // ─── Estado ─────────────────────────────────────────────────
    const [accounts, setAccounts] = useState<EmailAccount[]>([])
    const [loadingAccounts, setLoadingAccounts] = useState(true)
    const deepLinkProcessed = useRef(false)

    const [messages, setMessages] = useState<EmailMessageSummary[]>([])
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [totalMessages, setTotalMessages] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const messagesPerPage = 20

    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
        null,
    )
    const [readFilter, setReadFilter] = useState<ReadFilter>('all')
    const [folder, setFolder] = useState<EmailFolder>('INBOX')
    const [searchQuery, setSearchQuery] = useState('')

    const [selectedMessage, setSelectedMessage] =
        useState<EmailMessageDetail | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [threadMessages, setThreadMessages] = useState<
        EmailMessageDetail[]
    >([])

    // Rascunhos
    const [drafts, setDrafts] = useState<EmailDraftSummary[]>([])

    // Compose
    const [showCompose, setShowCompose] = useState(false)
    const [composeMode, setComposeMode] = useState<ComposeMode>('compose')
    const [composeOriginal, setComposeOriginal] =
        useState<EmailMessageDetail | null>(null)
    const [composeDraft, setComposeDraft] =
        useState<EmailDraftDetail | null>(null)

    const [mobileView, setMobileView] = useState<'list' | 'detail'>('list')

    const iframeRef = useRef<HTMLIFrameElement>(null)

    // ─── Carregar contas ────────────────────────────────────────
    useEffect(() => {
        loadAccounts()
    }, [])

    const loadAccounts = async () => {
        setLoadingAccounts(true)
        try {
            const response = await getEmailAccounts()
            if (response.success) {
                setAccounts(
                    (response.result || []).filter(
                        a => a.status === 'ACTIVE',
                    ),
                )
            }
        } catch {
            // silencioso
        } finally {
            setLoadingAccounts(false)
        }
    }

    // ─── Carregar mensagens ─────────────────────────────────────
    const loadMessages = useCallback(
        async (page = 1, silent = false) => {
            if (!silent) setLoadingMessages(true)
            try {
                const params: {
                    page: number
                    limit: number
                    isRead?: string
                    accountId?: number
                    query?: string
                    folder?: string
                } = { page, limit: messagesPerPage, folder }

                if (readFilter === 'unread') params.isRead = 'false'
                if (readFilter === 'read') params.isRead = 'true'
                if (selectedAccountId) params.accountId = selectedAccountId
                if (searchQuery) params.query = searchQuery

                const response = await getEmailMessages(params)
                if (response.success) {
                    setMessages(response.result || [])
                    setTotalMessages(response.total ?? 0)
                    if (response.pagination) {
                        setTotalPages(
                            response.pagination.pages?.length || 1,
                        )
                    } else {
                        setTotalPages(
                            Math.ceil(
                                (response.total ?? 0) / messagesPerPage,
                            ) || 1,
                        )
                    }
                }
            } catch {
                if (!silent) toast.error('Erro ao carregar mensagens')
            } finally {
                setLoadingMessages(false)
            }
        },
        [readFilter, selectedAccountId, messagesPerPage, folder, searchQuery],
    )

    // Carregar rascunhos quando folder = DRAFT
    const loadDrafts = useCallback(async () => {
        try {
            const response = await getDrafts(
                selectedAccountId ?? undefined,
            )
            if (response.success) {
                setDrafts(response.result || [])
            }
        } catch {
            // silencioso
        }
    }, [selectedAccountId])

    useEffect(() => {
        if (accounts.length > 0) {
            setCurrentPage(1)
            if (folder === 'DRAFT') {
                loadDrafts()
            } else {
                loadMessages(1)
            }
        } else if (!loadingAccounts) {
            setMessages([])
            setDrafts([])
            setLoadingMessages(false)
        }
    }, [
        accounts.length,
        loadingAccounts,
        readFilter,
        selectedAccountId,
        folder,
        searchQuery,
    ])

    // ─── Deep link via query params ─────────────────────────────
    useEffect(() => {
        if (
            deepLinkProcessed.current ||
            loadingAccounts ||
            accounts.length === 0
        )
            return

        const accountIdParam = searchParams.get('accountId')
        const messageIdParam = searchParams.get('messageId')

        if (!accountIdParam && !messageIdParam) return
        deepLinkProcessed.current = true

        if (accountIdParam) {
            const id = Number(accountIdParam)
            if (accounts.some(a => a.id === id)) {
                setSelectedAccountId(id)
            }
        }

        if (messageIdParam) {
            const id = Number(messageIdParam)
            openMessage({ id } as EmailMessageSummary)
        }

        setSearchParams({}, { replace: true })
    }, [loadingAccounts, accounts])

    // ─── Websocket: recarregar ao receber novo email ───────────
    useEffect(() => {
        if (!socket || accounts.length === 0) return

        const accountIds = accounts.map(a => a.id)

        const handleNotification = (notification: any) => {
            const emailAccountId = notification?.metadata?.emailAccountId
            if (emailAccountId && accountIds.includes(emailAccountId)) {
                if (folder === 'DRAFT') {
                    loadDrafts()
                } else {
                    loadMessages(currentPage, true)
                }
            }
        }

        socket.on('notification:new', handleNotification)

        return () => {
            socket.off('notification:new', handleNotification)
        }
    }, [socket, accounts, currentPage, loadMessages, loadDrafts, folder])

    // ─── Carregar detalhe ───────────────────────────────────────
    const openMessage = async (msg: EmailMessageSummary) => {
        setLoadingDetail(true)
        setSelectedMessage(null)
        setThreadMessages([])
        setMobileView('detail')

        try {
            const response = await getEmailMessage(msg.id)
            if (response.success && response.result) {
                const detail = response.result
                setSelectedMessage(detail)

                // Carregar thread se tiver threadId
                if (detail.threadId && detail.emailAccount?.id) {
                    try {
                        const threadRes = await getEmailThread(
                            detail.threadId,
                            detail.emailAccount.id,
                        )
                        if (
                            threadRes.success &&
                            threadRes.result &&
                            threadRes.result.length > 1
                        ) {
                            setThreadMessages(threadRes.result)
                        }
                    } catch {
                        // Sem thread, exibir mensagem unica
                    }
                }

                // Marcar como lido se nao lido
                if (!msg.isRead && canUpdate) {
                    await toggleEmailReadStatus(msg.id, true)
                    setMessages(prev =>
                        prev.map(m =>
                            m.id === msg.id ? { ...m, isRead: true } : m,
                        ),
                    )
                }
            } else {
                toast.error('Erro ao carregar mensagem')
            }
        } catch {
            toast.error('Erro ao carregar mensagem')
        } finally {
            setLoadingDetail(false)
        }
    }

    // Abrir rascunho
    const openDraft = async (draft: EmailDraftSummary) => {
        try {
            const response = await getDraft(draft.id)
            if (response.success && response.result) {
                setComposeDraft(response.result)
                setComposeMode('compose')
                setComposeOriginal(null)
                setShowCompose(true)
            }
        } catch {
            toast.error('Erro ao carregar rascunho')
        }
    }

    // ─── Acoes de mensagem ──────────────────────────────────────
    const handleToggleRead = async () => {
        if (!selectedMessage || !canUpdate) return

        const newIsRead = !selectedMessage.isRead
        try {
            const response = await toggleEmailReadStatus(
                selectedMessage.id,
                newIsRead,
            )
            if (response.success) {
                setMessages(prev =>
                    prev.map(m =>
                        m.id === selectedMessage.id
                            ? { ...m, isRead: newIsRead }
                            : m,
                    ),
                )
                setSelectedMessage(prev =>
                    prev ? { ...prev, isRead: newIsRead } : prev,
                )
            }
        } catch {
            toast.error('Erro ao alterar status de leitura')
        }
    }

    const handleToggleStar = async () => {
        if (!selectedMessage || !canUpdate) return

        const newStarred = !selectedMessage.isStarred
        try {
            const response = await toggleEmailStar(
                selectedMessage.id,
                newStarred,
            )
            if (response.success) {
                setMessages(prev =>
                    prev.map(m =>
                        m.id === selectedMessage.id
                            ? { ...m, isStarred: newStarred }
                            : m,
                    ),
                )
                setSelectedMessage(prev =>
                    prev ? { ...prev, isStarred: newStarred } : prev,
                )
            }
        } catch {
            toast.error('Erro ao alterar estrela')
        }
    }

    const handleArchive = async () => {
        if (!selectedMessage || !canUpdate) return
        try {
            const response = await archiveEmail(selectedMessage.id)
            if (response.success) {
                toast.success('Email arquivado')
                setMessages(prev =>
                    prev.filter(m => m.id !== selectedMessage.id),
                )
                setSelectedMessage(null)
                setMobileView('list')
            }
        } catch {
            toast.error('Erro ao arquivar')
        }
    }

    const handleTrash = async () => {
        if (!selectedMessage || !canUpdate) return
        try {
            const response = await trashEmail(selectedMessage.id)
            if (response.success) {
                toast.success('Email movido para lixeira')
                setMessages(prev =>
                    prev.filter(m => m.id !== selectedMessage.id),
                )
                setSelectedMessage(null)
                setMobileView('list')
            }
        } catch {
            toast.error('Erro ao mover para lixeira')
        }
    }

    const handleUntrash = async () => {
        if (!selectedMessage || !canUpdate) return
        try {
            const response = await untrashEmail(selectedMessage.id)
            if (response.success) {
                toast.success('Email restaurado da lixeira')
                setMessages(prev =>
                    prev.filter(m => m.id !== selectedMessage.id),
                )
                setSelectedMessage(null)
                setMobileView('list')
            }
        } catch {
            toast.error('Erro ao restaurar')
        }
    }

    const handleStarInList = async (
        msg: EmailMessageSummary,
        e: React.MouseEvent,
    ) => {
        e.stopPropagation()
        if (!canUpdate) return

        const newStarred = !msg.isStarred
        try {
            const response = await toggleEmailStar(msg.id, newStarred)
            if (response.success) {
                setMessages(prev =>
                    prev.map(m =>
                        m.id === msg.id
                            ? { ...m, isStarred: newStarred }
                            : m,
                    ),
                )
                if (selectedMessage?.id === msg.id) {
                    setSelectedMessage(prev =>
                        prev ? { ...prev, isStarred: newStarred } : prev,
                    )
                }
            }
        } catch {
            toast.error('Erro ao alterar estrela')
        }
    }

    // ─── Compose handlers ───────────────────────────────────────
    const handleCompose = () => {
        setComposeMode('compose')
        setComposeOriginal(null)
        setComposeDraft(null)
        setShowCompose(true)
    }

    const handleReply = (msg?: EmailMessageDetail) => {
        const target = msg || selectedMessage
        if (!target) return
        setComposeMode('reply')
        setComposeOriginal(target)
        setComposeDraft(null)
        setShowCompose(true)
    }

    const handleReplyAll = (msg?: EmailMessageDetail) => {
        const target = msg || selectedMessage
        if (!target) return
        setComposeMode('replyAll')
        setComposeOriginal(target)
        setComposeDraft(null)
        setShowCompose(true)
    }

    const handleForward = (msg?: EmailMessageDetail) => {
        const target = msg || selectedMessage
        if (!target) return
        setComposeMode('forward')
        setComposeOriginal(target)
        setComposeDraft(null)
        setShowCompose(true)
    }

    const handleSent = () => {
        if (folder === 'DRAFT') {
            loadDrafts()
        } else {
            loadMessages(currentPage)
        }
    }

    // ─── Folder change ──────────────────────────────────────────
    const handleFolderChange = (newFolder: EmailFolder) => {
        setFolder(newFolder)
        setSelectedMessage(null)
        setThreadMessages([])
        setCurrentPage(1)
        setMobileView('list')
    }

    const handleAccountChange = (accountId: number | null) => {
        setSelectedAccountId(accountId)
        setCurrentPage(1)
    }

    // ─── Paginacao ──────────────────────────────────────────────
    const goToPage = (page: number) => {
        if (page < 1 || page > totalPages) return
        setCurrentPage(page)
        loadMessages(page)
    }

    // ─── Sanitizar HTML ─────────────────────────────────────────
    const sanitizeHtml = (html: string): string => {
        return DOMPurify.sanitize(html, {
            ALLOWED_TAGS: [
                'a', 'b', 'br', 'blockquote', 'center', 'code', 'div',
                'em', 'font', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span',
                'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr',
                'u', 'ul', 'style', 'head', 'body', 'html',
            ],
            ALLOWED_ATTR: [
                'href', 'src', 'alt', 'title', 'style', 'class',
                'width', 'height', 'border', 'cellpadding', 'cellspacing',
                'align', 'valign', 'bgcolor', 'color', 'face', 'size',
                'target', 'colspan', 'rowspan', 'dir',
            ],
            ALLOW_DATA_ATTR: false,
            ADD_ATTR: ['target'],
        })
    }

    const getIframeContent = (message: EmailMessageDetail): string => {
        const html = message.bodyHtml || ''
        const sanitized = sanitizeHtml(html)
        return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <base target="_blank" rel="noopener noreferrer" />
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            color: #333;
            margin: 0;
            padding: 16px;
            word-wrap: break-word;
            overflow-wrap: break-word;
        }
        img { max-width: 100%; height: auto; }
        a { color: #1a73e8; }
        blockquote {
            border-left: 3px solid #ddd;
            margin: 8px 0;
            padding: 4px 12px;
            color: #666;
        }
        table { max-width: 100%; }
        pre { overflow-x: auto; }
    </style>
</head>
<body>${sanitized}</body>
</html>`
    }

    const handleIframeLoad = () => {
        const iframe = iframeRef.current
        if (!iframe) return
        try {
            const doc =
                iframe.contentDocument || iframe.contentWindow?.document
            if (doc?.body) {
                iframe.style.height = doc.body.scrollHeight + 32 + 'px'
            }
        } catch {
            // cross-origin
        }
    }

    // ─── Estado vazio ───────────────────────────────────────────
    if (!loadingAccounts && accounts.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[500px] gap-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                    <MdEmail className="text-5xl text-secondary/40" />
                </div>
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-base-content mb-2">
                        Caixa de Entrada
                    </h2>
                    <p className="text-base-content/60 max-w-md">
                        Nenhuma conta de email conectada. Conecte uma conta do
                        Gmail para centralizar seus emails aqui.
                    </p>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/administrative/inbox/contas')}
                    >
                        <FaGoogle className="text-lg" />
                        Conectar Gmail
                    </button>
                )}
            </div>
        )
    }

    // ─── Loading inicial ────────────────────────────────────────
    if (loadingAccounts) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando caixa de entrada...
                </p>
            </div>
        )
    }

    // ─── Contagem nao lidos ─────────────────────────────────────
    const unreadCount = messages.filter(m => !m.isRead).length
    const isTrashed = folder === 'TRASH'
    const isDraftFolder = folder === 'DRAFT'
    const showThread = threadMessages.length > 1

    return (
        <div className="flex flex-col h-[calc(100vh-10rem)]">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 pb-4 border-b border-base-300/50">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-md shadow-secondary/20">
                        <MdEmail className="text-xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-base-content">
                            {FOLDER_TITLES[folder]}
                        </h1>
                        <p className="text-xs text-base-content/50">
                            {isDraftFolder
                                ? `${drafts.length} rascunho${drafts.length !== 1 ? 's' : ''}`
                                : `${totalMessages} ${totalMessages === 1 ? 'mensagem' : 'mensagens'}`}
                            {!isDraftFolder && unreadCount > 0 && (
                                <span className="ml-1 text-secondary font-medium">
                                    ({unreadCount} não{' '}
                                    {unreadCount === 1 ? 'lida' : 'lidas'})
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm btn-square"
                        onClick={() => {
                            if (isDraftFolder) {
                                loadDrafts()
                            } else {
                                loadMessages(currentPage)
                            }
                            loadAccounts()
                        }}
                        title="Atualizar"
                    >
                        <MdRefresh className="text-lg" />
                    </button>
                    <button
                        className="btn btn-ghost btn-sm gap-1"
                        onClick={() => navigate('/administrative/inbox/templates')}
                        title="Modelos de email"
                    >
                        <MdDescription className="text-lg" />
                        <span className="hidden sm:inline text-xs">
                            Modelos
                        </span>
                    </button>
                    <button
                        className="btn btn-ghost btn-sm gap-1"
                        onClick={() => navigate('/administrative/inbox/contas')}
                        title="Gerenciar contas"
                    >
                        <MdSettings className="text-lg" />
                        <span className="hidden sm:inline text-xs">
                            Contas
                        </span>
                    </button>
                </div>
            </div>

            {/* Conteudo principal */}
            <div className="flex flex-1 min-h-0 mt-4 gap-0 rounded-xl border border-base-300/50 overflow-hidden bg-base-100">
                {/* Sidebar de pastas */}
                <div className="hidden lg:flex flex-col w-56 border-r border-base-300/50 bg-base-200/30 shrink-0 p-3 overflow-y-auto">
                    <FolderSidebar
                        selectedFolder={folder}
                        onFolderChange={handleFolderChange}
                        accounts={accounts}
                        selectedAccountId={selectedAccountId}
                        onAccountChange={handleAccountChange}
                        onCompose={handleCompose}
                        canCreate={canCreate}
                    />
                </div>

                {/* Lista de mensagens */}
                <div
                    className={`flex flex-col w-full lg:w-96 border-r border-base-300/50 shrink-0 ${
                        mobileView === 'detail' && selectedMessage
                            ? 'hidden lg:flex'
                            : 'flex'
                    }`}
                >
                    {/* Busca */}
                    {!isDraftFolder && (
                        <div className="p-3 border-b border-base-300/30">
                            <SearchBar
                                value={searchQuery}
                                onChange={val => {
                                    setSearchQuery(val)
                                    setCurrentPage(1)
                                }}
                            />
                        </div>
                    )}

                    {/* Filtros */}
                    {!isDraftFolder && (
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-base-300/30">
                            {/* Seletor de conta e pasta mobile */}
                            <select
                                className="select select-xs select-bordered lg:hidden flex-1 min-w-0"
                                value={folder}
                                onChange={e =>
                                    handleFolderChange(
                                        e.target.value as EmailFolder,
                                    )
                                }
                            >
                                {Object.entries(FOLDER_TITLES).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ),
                                )}
                            </select>

                            {/* Filtros de leitura */}
                            <div className="flex gap-1 ml-auto">
                                {(
                                    [
                                        { key: 'all', label: 'Todas' },
                                        {
                                            key: 'unread',
                                            label: 'Não lidas',
                                        },
                                        { key: 'read', label: 'Lidas' },
                                    ] as const
                                ).map(f => (
                                    <button
                                        key={f.key}
                                        className={`btn btn-xs ${
                                            readFilter === f.key
                                                ? 'btn-secondary'
                                                : 'btn-ghost'
                                        }`}
                                        onClick={() => {
                                            setReadFilter(f.key)
                                            setCurrentPage(1)
                                        }}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Lista */}
                    <div className="flex-1 overflow-y-auto">
                        {isDraftFolder ? (
                            // Lista de rascunhos
                            drafts.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                    <MdMailOutline className="text-4xl text-base-content/20 mb-3" />
                                    <p className="text-sm text-base-content/50">
                                        Nenhum rascunho
                                    </p>
                                </div>
                            ) : (
                                drafts.map(draft => (
                                    <button
                                        key={draft.id}
                                        className="w-full flex items-start gap-3 px-4 py-3 text-left border-b border-base-300/20 hover:bg-base-200/50 transition-colors"
                                        onClick={() => openDraft(draft)}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-base-content truncate">
                                                {draft.toAddresses ||
                                                    '(Sem destinatário)'}
                                            </p>
                                            <p className="text-sm text-base-content/60 truncate mt-0.5">
                                                {draft.subject ||
                                                    '(Sem assunto)'}
                                            </p>
                                            <p className="text-xs text-base-content/40 mt-1">
                                                {formatMessageDate(
                                                    draft.updatedAt,
                                                )}
                                            </p>
                                        </div>
                                    </button>
                                ))
                            )
                        ) : loadingMessages ? (
                            <div className="flex items-center justify-center py-16">
                                <span className="loading loading-spinner loading-md text-secondary"></span>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                <MdMailOutline className="text-4xl text-base-content/20 mb-3" />
                                <p className="text-sm text-base-content/50">
                                    {searchQuery
                                        ? 'Nenhuma mensagem encontrada para esta pesquisa'
                                        : readFilter === 'unread'
                                          ? 'Nenhuma mensagem não lida'
                                          : readFilter === 'read'
                                            ? 'Nenhuma mensagem lida'
                                            : 'Nenhuma mensagem encontrada'}
                                </p>
                            </div>
                        ) : (
                            messages.map(msg => (
                                <button
                                    key={msg.id}
                                    className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-base-300/20 hover:bg-base-200/50 transition-colors ${
                                        selectedMessage?.id === msg.id
                                            ? 'bg-secondary/5'
                                            : ''
                                    } ${!msg.isRead ? 'bg-base-200/30' : ''}`}
                                    onClick={() => openMessage(msg)}
                                >
                                    {/* Indicador nao lido + estrela */}
                                    <div className="flex flex-col items-center gap-1 pt-1 shrink-0">
                                        {!msg.isRead ? (
                                            <div className="w-2.5 h-2.5 rounded-full bg-secondary"></div>
                                        ) : (
                                            <div className="w-2.5 h-2.5"></div>
                                        )}
                                        {canUpdate && (
                                            <button
                                                onClick={e =>
                                                    handleStarInList(msg, e)
                                                }
                                                className="btn btn-ghost btn-xs btn-circle p-0 min-h-0 h-auto"
                                                title={
                                                    msg.isStarred
                                                        ? 'Remover estrela'
                                                        : 'Adicionar estrela'
                                                }
                                            >
                                                {msg.isStarred ? (
                                                    <MdStar className="text-sm text-warning" />
                                                ) : (
                                                    <MdStarBorder className="text-sm text-base-content/20" />
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {/* Conteudo */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <p
                                                className={`text-sm truncate ${
                                                    !msg.isRead
                                                        ? 'font-semibold text-base-content'
                                                        : 'text-base-content/80'
                                                }`}
                                            >
                                                {msg.fromName ||
                                                    msg.fromAddress ||
                                                    'Desconhecido'}
                                            </p>
                                            <span className="text-xs text-base-content/40 shrink-0">
                                                {formatMessageDate(
                                                    msg.receivedAt,
                                                )}
                                            </span>
                                        </div>
                                        <p
                                            className={`text-sm truncate mt-0.5 ${
                                                !msg.isRead
                                                    ? 'font-medium text-base-content/90'
                                                    : 'text-base-content/60'
                                            }`}
                                        >
                                            {msg.subject || '(Sem assunto)'}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <p className="text-xs text-base-content/40 truncate flex-1">
                                                {msg.snippet || ''}
                                            </p>
                                            {msg.hasAttachments && (
                                                <MdAttachFile className="text-xs text-base-content/30 shrink-0" />
                                            )}
                                        </div>
                                        {!selectedAccountId &&
                                            msg.emailAccount && (
                                                <p className="text-xs text-base-content/30 mt-1 truncate">
                                                    {msg.emailAccount.email}
                                                </p>
                                            )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    {/* Paginacao */}
                    {!isDraftFolder && totalPages > 1 && (
                        <div className="flex items-center justify-between px-3 py-2 border-t border-base-300/30 bg-base-200/20">
                            <button
                                className="btn btn-ghost btn-xs btn-square"
                                disabled={currentPage <= 1}
                                onClick={() => goToPage(currentPage - 1)}
                            >
                                <MdNavigateBefore className="text-lg" />
                            </button>
                            <span className="text-xs text-base-content/50">
                                {currentPage} de {totalPages}
                            </span>
                            <button
                                className="btn btn-ghost btn-xs btn-square"
                                disabled={currentPage >= totalPages}
                                onClick={() => goToPage(currentPage + 1)}
                            >
                                <MdNavigateNext className="text-lg" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Detalhe da mensagem */}
                <div
                    className={`flex-1 flex flex-col min-w-0 ${
                        mobileView === 'list' && !selectedMessage
                            ? 'hidden lg:flex'
                            : mobileView === 'list'
                              ? 'hidden lg:flex'
                              : 'flex'
                    }`}
                >
                    {loadingDetail ? (
                        <div className="flex-1 flex items-center justify-center">
                            <span className="loading loading-spinner loading-md text-secondary"></span>
                        </div>
                    ) : selectedMessage ? (
                        <>
                            {/* Header do detalhe */}
                            <div className="flex items-start gap-3 p-4 border-b border-base-300/30">
                                {/* Voltar (mobile) */}
                                <button
                                    className="btn btn-ghost btn-sm btn-square shrink-0 lg:hidden"
                                    onClick={() => {
                                        setMobileView('list')
                                        setSelectedMessage(null)
                                        setThreadMessages([])
                                    }}
                                >
                                    <MdChevronLeft className="text-xl" />
                                </button>

                                <div className="flex-1 min-w-0">
                                    <h2 className="text-base font-semibold text-base-content leading-snug">
                                        {selectedMessage.subject ||
                                            '(Sem assunto)'}
                                    </h2>

                                    {/* Acoes da mensagem */}
                                    <div className="mt-2">
                                        <MessageActions
                                            isStarred={
                                                selectedMessage.isStarred
                                            }
                                            isRead={selectedMessage.isRead}
                                            isTrashed={isTrashed}
                                            onReply={() => handleReply()}
                                            onReplyAll={() =>
                                                handleReplyAll()
                                            }
                                            onForward={() =>
                                                handleForward()
                                            }
                                            onToggleStar={handleToggleStar}
                                            onArchive={handleArchive}
                                            onTrash={handleTrash}
                                            onUntrash={handleUntrash}
                                            onToggleRead={handleToggleRead}
                                            canUpdate={canUpdate}
                                            canCreate={canCreate}
                                        />
                                    </div>
                                </div>

                                <button
                                    className="btn btn-ghost btn-sm btn-square hidden lg:flex shrink-0"
                                    onClick={() => {
                                        setSelectedMessage(null)
                                        setThreadMessages([])
                                    }}
                                    title="Fechar"
                                >
                                    <MdClose className="text-lg" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="flex-1 overflow-y-auto">
                                {showThread ? (
                                    <div className="p-4">
                                        <ThreadView
                                            messages={threadMessages}
                                            currentAccount={
                                                accounts.find(
                                                    a =>
                                                        a.id ===
                                                        selectedMessage
                                                            .emailAccount?.id,
                                                ) || accounts[0]
                                            }
                                            onReply={handleReply}
                                            onReplyAll={handleReplyAll}
                                            onForward={handleForward}
                                            canCreate={canCreate}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        {/* Remetente info */}
                                        <div className="px-4 pt-4 pb-2">
                                            <div className="flex items-center gap-2">
                                                {selectedMessage.emailAccount
                                                    ?.picture ? (
                                                    <img
                                                        src={
                                                            selectedMessage
                                                                .emailAccount
                                                                .picture
                                                        }
                                                        alt=""
                                                        className="w-8 h-8 rounded-full shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                                        <MdEmail className="text-sm text-secondary" />
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-base-content">
                                                        {selectedMessage.fromName ||
                                                            selectedMessage.fromAddress}
                                                        {selectedMessage.fromName &&
                                                            selectedMessage.fromAddress && (
                                                                <span className="text-base-content/40 font-normal ml-1">
                                                                    &lt;
                                                                    {
                                                                        selectedMessage.fromAddress
                                                                    }
                                                                    &gt;
                                                                </span>
                                                            )}
                                                    </p>
                                                    <p className="text-xs text-base-content/50">
                                                        Para:{' '}
                                                        {selectedMessage.toAddresses ||
                                                            selectedMessage
                                                                .emailAccount
                                                                ?.email ||
                                                            ''}
                                                        {selectedMessage.ccAddresses && (
                                                            <span className="ml-2">
                                                                Cc:{' '}
                                                                {
                                                                    selectedMessage.ccAddresses
                                                                }
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-xs text-base-content/40 mt-1 ml-10">
                                                {formatFullDate(
                                                    selectedMessage.receivedAt,
                                                )}
                                            </p>
                                        </div>

                                        {/* Corpo do email */}
                                        {selectedMessage.bodyHtml ? (
                                            <iframe
                                                ref={iframeRef}
                                                srcDoc={getIframeContent(
                                                    selectedMessage,
                                                )}
                                                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                                                className="w-full border-0 min-h-[300px]"
                                                style={{ height: '100%' }}
                                                onLoad={handleIframeLoad}
                                                title="Conteúdo do email"
                                            />
                                        ) : selectedMessage.bodyText ? (
                                            <div className="p-4">
                                                <pre className="text-sm text-base-content/80 whitespace-pre-wrap font-sans leading-relaxed">
                                                    {
                                                        selectedMessage.bodyText
                                                    }
                                                </pre>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center py-16">
                                                <p className="text-sm text-base-content/40">
                                                    Sem conteúdo
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Anexos (somente na visao single, nao thread) */}
                            {!showThread &&
                                selectedMessage.attachments &&
                                selectedMessage.attachments.length > 0 && (
                                    <div className="border-t border-base-300/30 p-4">
                                        <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-3">
                                            <MdAttachFile className="inline text-sm mr-1" />
                                            {
                                                selectedMessage.attachments
                                                    .length
                                            }{' '}
                                            {selectedMessage.attachments
                                                .length === 1
                                                ? 'anexo'
                                                : 'anexos'}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedMessage.attachments.map(
                                                (
                                                    att: EmailAttachmentInfo,
                                                ) => (
                                                    <a
                                                        key={att.id}
                                                        href={getAttachmentDownloadUrl(
                                                            selectedMessage.id,
                                                            att.id,
                                                        )}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-base-200/50 border border-base-300/30 hover:bg-base-200 transition-colors group"
                                                    >
                                                        <MdDownload className="text-base text-base-content/40 group-hover:text-secondary transition-colors" />
                                                        <div className="min-w-0">
                                                            <p className="text-xs font-medium text-base-content truncate max-w-[200px]">
                                                                {att.filename ||
                                                                    'Anexo'}
                                                            </p>
                                                            {att.size && (
                                                                <p className="text-xs text-base-content/40">
                                                                    {formatFileSize(
                                                                        att.size,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </a>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                            <MdMailOutline className="text-6xl text-base-content/10 mb-4" />
                            <p className="text-base-content/40 text-sm">
                                Selecione uma mensagem para visualizar
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Compose Modal */}
            <ComposeModal
                isOpen={showCompose}
                onClose={() => {
                    setShowCompose(false)
                    setComposeDraft(null)
                }}
                onSent={handleSent}
                accounts={accounts}
                mode={composeMode}
                originalMessage={composeOriginal}
                draftData={composeDraft}
            />
        </div>
    )
}
